import React, { memo, useRef, useEffect, useState, useCallback } from 'react';
import { dates, formats, validators } from 'investira.sdk';
import { Stack, Card, Icon } from 'investira.react.components';
import { StocksChart } from 'investira.react.charts';
import Controls from '../AtivoCharts/Controls';

const AtivoCotacao = memo(props => {
  const { ativo, cotacoes, isIndicadorLoading } = props;
  const chartRef = useRef(null);

  function formatData(pData = [], pAjustado = true, pType = 0) {
    if (validators.isNull(pData)) {
      return [];
    }

    const xCotacao = pAjustado ? 'cotacao_ajustada' : 'cotacao';
    const xTypes = [xCotacao];

    const xFormattedData = pData.map(xPoint => {
      const xDate = dates.toDate(xPoint.data).getTime();
      const xType = xTypes[pType];
      return [xDate, xPoint[xType]];
    });

    return xFormattedData;
  }

  const createChartOptions = useCallback((pCotacoes, pAjustado = true, pType = 0) => {
    const to = new Date().getTime();
    const from = new Date(to - 365 * 24 * 3600 * 1000).getTime();

    const xSeries = pCotacoes.map(pCotacao => {
      const xData = pCotacao.data;

      return {
        name: pCotacao.ativo,
        type: 'line',
        data: formatData(xData, pAjustado, pType)
      };
    });

    const xOptions = {
      title: false,
      chart: {
        zooming: {
          type: 'x'
        },
        events: {
          load: function () {
            const chart = this;
            chart.xAxis[0].setExtremes(from, to);
          }
        }
      },
      rangeSelector: {
        enabled: false,
        buttons: [
          {
            type: 'month',
            count: 1,
            text: '1M'
          },
          {
            type: 'month',
            count: 6,
            text: '6M'
          },
          {
            type: 'year',
            count: 1,
            text: '1A'
          },
          {
            type: 'year',
            count: 2,
            text: '2A'
          },
          {
            type: 'year',
            count: 3,
            text: '3A'
          },
          {
            type: 'year',
            count: 5,
            text: '5A'
          },
          {
            type: 'ytd',
            text: 'YTD'
          },
          {
            type: 'all',
            text: 'MÁX.'
          }
        ],
        selected: 2
      },
      xAxis: { type: 'datetime', crosshair: true },
      yAxis: {
        type: 'linear',
        opposite: false,
        title: false,
        labels: {
          formatter: function () {
            return `R$ ${this.value}`;
          }
        }
      },
      series: xSeries,
      tooltip: {
        formatter: function (_tooltip) {
          const xDate = formats.formatDateCustom(this.point.x, 'DD/MMM/YYYY');
          const xDecimais = ativo.decimais > 6 ? 6 : ativo.decimais;
          const xCotacao = formats.friendlyNumber(this.point.y, xDecimais, true);

          return `
          <span style="font-size: 1em"><b>${xDate}</b></span><br/>
          <span style="color:${this.color}">\u25CF</span>
          ${this.series.name}: <b>${xCotacao}</b>
          `;
        }
      }
    };

    return xOptions;
  }, []);

  const [ajustado, setAjustado] = useState(true);
  const [type, setType] = useState(0);
  const [options, setOptions] = useState(null);

  const handleRangeSelect = (type, count) => {
    const xChart = chartRef.current;
    const xTo = new Date().getTime();
    let xFrom;

    switch (type) {
      case 'month':
        xFrom = new Date(xTo - count * 30 * 24 * 3600 * 1000).getTime();
        break;
      case 'year':
        xFrom = new Date(xTo - count * 365 * 24 * 3600 * 1000).getTime();
        break;
      case 'ytd':
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        xFrom = startOfYear;
        break;
      case 'all':
        xFrom = null;
        break;
      default:
        return;
    }

    xChart.xAxis[0].setExtremes(xFrom, xTo);
  };

  useEffect(() => {
    const xOptions = createChartOptions(cotacoes, ajustado, type);

    setOptions(xOptions);
  }, [cotacoes, ajustado, type, createChartOptions]);

  if (validators.isEmpty(cotacoes[0].data)) {
    return null;
  }

  if (validators.isNull(options)) {
    return null;
  }

  return (
    <Stack>
      <Card>
        <Stack>
          <Stack
            flexGrow={1}
            py={2}
            px={2}
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}>
            <Stack>
              <Icon iconName="chart_line" color="primary" size={21} />
            </Stack>
            <Stack flexGrow="1"></Stack>
            <Controls
              range={{
                selected: options.rangeSelector.selected,
                buttons: options.rangeSelector.buttons,
                setRange: handleRangeSelect
              }}
              ajustado={{
                onChange: setAjustado
              }}
              type={{
                value: type,
                onChange: setType
              }}
            />
          </Stack>
          <Stack sx={{ px: 2, py: 2 }}>
            <StocksChart
              key={'cotacao'}
              ref={chartRef}
              options={options}
              isLoading={isIndicadorLoading}
            />
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
});

export default AtivoCotacao;
